import quoteUtils from "@/modules/quotes/components/utils";
import moneyService from "@/services/money";
import {
  flattenPageImageBlocks,
  mapApiBlockToForm,
  mapFormBlockToApi,
  migrateFlatPageImages,
} from "@/modules/quotes/components/program/programImageBlocks";
import { normalizePageContent, normalizePageType } from "@/modules/program_pages/utils";

function mapExtraPageToForm(page) {
  if (!page || typeof page !== "object") return null;
  if (page.mode === "detached") {
    const type = normalizePageType(page.type);
    return {
      mode: "detached",
      sourcePageId: page.source_page_id ?? page.sourcePageId ?? null,
      title: page.title || "",
      type,
      content: normalizePageContent(type, page.content),
    };
  }
  const pageId = page.page_id ?? page.pageId ?? null;
  if (!pageId) return null;
  return {
    mode: "live",
    pageId,
  };
}

function mapExtraPageToApi(page) {
  if (!page || typeof page !== "object") return null;
  if (page.mode === "detached") {
    const type = normalizePageType(page.type);
    return {
      mode: "detached",
      source_page_id: page.sourcePageId ?? page.source_page_id ?? null,
      title: page.title || "",
      type,
      content: normalizePageContent(type, page.content),
    };
  }
  const pageId = page.pageId ?? page.page_id ?? null;
  if (!pageId) return null;
  return {
    mode: "live",
    page_id: pageId,
  };
}

/**
 * @typedef {Object} QuoteMargin
 * @property {string} [status]
 * @property {number} [percentFixed]
 * @property {number} [percent_fixed]
 * @property {number[]} [percentDynamic]
 * @property {number[]} [percent_dynamic]
 * @property {number[]} [perPax]
 * @property {number[]} [per_pax]
 * @property {number[]} [perGroup]
 * @property {number[]} [per_group]
 */

function createEmptyMargin() {
  return {
    status: "fixed",
    percentFixed: 0,
    /** @type {number[]} */
    percentDynamic: [],
    /** @type {number[]} */
    perPax: [],
    /** @type {number[]} */
    perGroup: [],
  };
}

/**
 * @param {QuoteMargin | null} [margin]
 * @returns {QuoteMargin}
 */
function cloneMargin(margin) {
  /** @type {QuoteMargin} */
  const source = margin || createEmptyMargin();
  return {
    status: source.status || "fixed",
    percentFixed: source.percentFixed ?? source.percent_fixed ?? 0,
    percentDynamic: [...(source.percentDynamic || source.percent_dynamic || [])],
    perPax: [...(source.perPax || source.per_pax || [])],
    perGroup: [...(source.perGroup || source.per_group || [])],
  };
}

/**
 * @param {QuoteMargin | null} [margin]
 * @returns {QuoteMargin | null}
 */
function mapComparisonMarginToFormData(margin) {
  if (!margin) return null;
  return cloneMargin(margin);
}

/**
 * @param {QuoteMargin | null} [margin]
 * @returns {{
 *   status: string,
 *   percent_fixed: number,
 *   percent_dynamic: number[],
 *   per_pax: number[],
 *   per_group: number[],
 * } | null}
 */
function mapComparisonMarginToBody(margin) {
  if (!margin) return null;
  return {
    status: margin.status || "fixed",
    percent_fixed: margin.percentFixed ?? 0,
    percent_dynamic: [...(margin.percentDynamic || [])],
    per_pax: [...(margin.perPax || [])],
    per_group: [...(margin.perGroup || [])],
  };
}

/**
 * @param {*} [productCost]
 */
function cloneProductCost(productCost) {
  if (!productCost) {
    return {
      type: "custom",
      currency: null,
      value: { default: 0, single: null, twin: null, triple: null, quad: null },
      confirm: false,
    };
  }

  return {
    type: productCost.type ?? "custom",
    currency: productCost.currency ?? productCost.cur ?? null,
    value: {
      default: productCost.value?.default ?? 0,
      single: productCost.value?.single ?? null,
      twin: productCost.value?.twin ?? null,
      triple: productCost.value?.triple ?? null,
      quad: productCost.value?.quad ?? null,
    },
    valueIncludesPorterage: productCost.valueIncludesPorterage,
    porterage: productCost.porterage,
    confirm: productCost.confirm ?? false,
    rateKind: productCost.rateKind,
  };
}

/**
 * @param {*} [override]
 */
function mapCostOverrideToFormData(override) {
  if (!override) return null;

  return {
    productCost: cloneProductCost(override.productCost ?? override.product_cost),
    isProductEditableCost: override.isProductEditableCost ?? override.product_cost_is_editable ?? true,
  };
}

/**
 * @param {*} [override]
 */
function mapCostOverrideToBody(override) {
  if (!override) return null;

  return {
    product_cost: cloneProductCost(override.productCost ?? override.product_cost),
    product_cost_is_editable: override.isProductEditableCost ?? override.product_cost_is_editable ?? true,
  };
}

/**
 * @param {*} supplierProductData
 * @param {*} supplierId
 * @param {*} productId
 */
function lookupSupplierProduct(supplierProductData, supplierId, productId) {
  const entry = supplierId != null ? supplierProductData?.[supplierId] : null;
  return {
    supplierInfo: entry?.supplier || null,
    productInfo: productId != null ? entry?.products?.[productId] || null : null,
  };
}

/**
 * @param {*} itemProductCost
 * @param {*} productType
 */
function parseStoredProductCost(itemProductCost, productType) {
  const roomDivisor = productType === "ROOM" ? 2 : 1;

  return {
    type: itemProductCost.type,
    currency: itemProductCost.cur,
    value: {
      default: itemProductCost.value.default
        ? moneyService._num(parseFloat(itemProductCost.value.default.substring(1)) / roomDivisor)
        : null,
      single: itemProductCost.value.single
        ? moneyService._num(parseFloat(itemProductCost.value.single.substring(1)) / roomDivisor)
        : null,
      twin: itemProductCost.value.twin
        ? moneyService._num(parseFloat(itemProductCost.value.twin.substring(1)) / roomDivisor)
        : null,
      triple: itemProductCost.value.triple
        ? moneyService._num(parseFloat(itemProductCost.value.triple.substring(1)) / roomDivisor)
        : null,
      quad: itemProductCost.value.quad
        ? moneyService._num(parseFloat(itemProductCost.value.quad.substring(1)) / roomDivisor)
        : null,
    },
    porterage: itemProductCost.porterage,
    valueIncludesPorterage: itemProductCost.valueIncludesPorterage,
    confirm: itemProductCost.confirm,
    rateKind: itemProductCost.rateKind,
  };
}

/**
 * @param {*} [overrides]
 * @returns {Record<string, any>}
 */
function mapCostOverridesMapToFormData(overrides) {
  const source = overrides && typeof overrides === "object" ? overrides : {};
  /** @type {Record<string, any>} */
  const result = {};

  Object.entries(source).forEach(([uuid, override]) => {
    const mapped = mapCostOverrideToFormData(override);
    if (mapped) result[uuid] = mapped;
  });

  return result;
}

/**
 * @param {*} [overrides]
 * @returns {Record<string, any> | undefined}
 */
function mapCostOverridesMapToBody(overrides) {
  const source = overrides && typeof overrides === "object" ? overrides : {};
  /** @type {Record<string, any>} */
  const result = {};

  Object.entries(source).forEach(([uuid, override]) => {
    const mapped = mapCostOverrideToBody(override);
    if (mapped) result[uuid] = mapped;
  });

  return Object.keys(result).length ? result : undefined;
}

/**
 * @param {QuoteMargin | null} [margin]
 */
function createEmptyComparisonPeriod(margin = null) {
  return {
    periodName: null,
    period: {
      startYear: null,
      startMonth: null,
      startDay: null,
      endYear: null,
      endMonth: null,
      endDay: null,
    },
    cityStart: null,
    cityEnd: null,
    margin: margin ? cloneMargin(margin) : null,
    costOverrides: {},
    logisticsCostOverrides: {},
    supplementTripleOverride: null,
    supplementQuadOverride: null,
  };
}

/**
 * @param {*} value
 * @returns {number | null}
 */
function normalizeSupplementOverride(value) {
  return value != null && value !== "" && !Number.isNaN(Number(value)) ? Number(value) : null;
}

/**
 * @param {*} [comparisonPeriod]
 */
function mapComparisonPeriodToFormData(comparisonPeriod) {
  const emptyComparisonPeriod = createEmptyComparisonPeriod();
  const mappedMargin = mapComparisonMarginToFormData(comparisonPeriod?.margin);
  const costOverrides = mapCostOverridesMapToFormData(
    comparisonPeriod?.costOverrides ?? comparisonPeriod?.cost_overrides
  );
  const logisticsCostOverrides = mapCostOverridesMapToFormData(
    comparisonPeriod?.logisticsCostOverrides ?? comparisonPeriod?.logistics_cost_overrides
  );
  const supplementTripleOverride = normalizeSupplementOverride(
    comparisonPeriod?.supplementTripleOverride ?? comparisonPeriod?.supplement_triple_override
  );
  const supplementQuadOverride = normalizeSupplementOverride(
    comparisonPeriod?.supplementQuadOverride ?? comparisonPeriod?.supplement_quad_override
  );

  if (comparisonPeriod?.period) {
    return {
      ...emptyComparisonPeriod,
      ...comparisonPeriod,
      period: {
        ...emptyComparisonPeriod.period,
        ...comparisonPeriod.period,
      },
      margin: mappedMargin,
      costOverrides,
      logisticsCostOverrides,
      supplementTripleOverride,
      supplementQuadOverride,
    };
  }

  if (comparisonPeriod?.start_date || comparisonPeriod?.end_date) {
    return {
      periodName: comparisonPeriod.period_name ?? null,
      period: {
        startYear: comparisonPeriod.start_date?.year ?? null,
        startMonth: comparisonPeriod.start_date?.month ?? null,
        startDay: comparisonPeriod.start_date?.day ?? null,
        endYear: comparisonPeriod.end_date?.year ?? null,
        endMonth: comparisonPeriod.end_date?.month ?? null,
        endDay: comparisonPeriod.end_date?.day ?? null,
      },
      cityStart: comparisonPeriod.city_start ?? comparisonPeriod.cityStart ?? null,
      cityEnd: comparisonPeriod.city_end ?? comparisonPeriod.cityEnd ?? null,
      margin: mappedMargin,
      costOverrides,
      logisticsCostOverrides,
      supplementTripleOverride,
      supplementQuadOverride,
    };
  }

  return {
    periodName: comparisonPeriod?.periodName ?? null,
    period: {
      startYear: comparisonPeriod?.startYear ?? null,
      startMonth: comparisonPeriod?.startMonth ?? null,
      startDay: comparisonPeriod?.startDay ?? null,
      endYear: comparisonPeriod?.endYear ?? null,
      endMonth: comparisonPeriod?.endMonth ?? null,
      endDay: comparisonPeriod?.endDay ?? null,
    },
    cityStart: comparisonPeriod?.cityStart ?? null,
    cityEnd: comparisonPeriod?.cityEnd ?? null,
    margin: mappedMargin,
    costOverrides,
    logisticsCostOverrides,
    supplementTripleOverride,
    supplementQuadOverride,
  };
}

/**
 * @param {*} comparisonPeriod
 */
function mapComparisonPeriodToBody(comparisonPeriod) {
  const period = comparisonPeriod.period || {};
  /** @type {Record<string, any>} */
  const body = {
    period_name: comparisonPeriod.periodName,
    start_date: {
      day: period.startDay,
      month: period.startMonth,
      year: period.startYear,
    },
    end_date: {
      day: period.endDay,
      month: period.endMonth,
      year: period.endYear,
    },
    city_start: comparisonPeriod.cityStart,
    city_end: comparisonPeriod.cityEnd,
    margin: mapComparisonMarginToBody(comparisonPeriod.margin),
    supplement_triple_override: normalizeSupplementOverride(
      comparisonPeriod.supplementTripleOverride ?? comparisonPeriod.supplement_triple_override
    ),
    supplement_quad_override: normalizeSupplementOverride(
      comparisonPeriod.supplementQuadOverride ?? comparisonPeriod.supplement_quad_override
    ),
  };

  const costOverrides = mapCostOverridesMapToBody(comparisonPeriod.costOverrides);
  const logisticsCostOverrides = mapCostOverridesMapToBody(comparisonPeriod.logisticsCostOverrides);
  if (costOverrides) body.cost_overrides = costOverrides;
  if (logisticsCostOverrides) body.logistics_cost_overrides = logisticsCostOverrides;

  return body;
}

/**
 * @param {*} formData
 */
function ensureComparisonPeriodsHaveMargins(formData) {
  if (!Array.isArray(formData.comparisonPeriods)) return;

  formData.comparisonPeriods = formData.comparisonPeriods.map((/** @type {*} */ comparisonPeriod) => ({
    ...comparisonPeriod,
    margin: comparisonPeriod?.margin ? cloneMargin(comparisonPeriod.margin) : cloneMargin(formData.margin),
    costOverrides: mapCostOverridesMapToFormData(comparisonPeriod?.costOverrides ?? comparisonPeriod?.cost_overrides),
    logisticsCostOverrides: mapCostOverridesMapToFormData(
      comparisonPeriod?.logisticsCostOverrides ?? comparisonPeriod?.logistics_cost_overrides
    ),
    supplementTripleOverride: normalizeSupplementOverride(
      comparisonPeriod?.supplementTripleOverride ?? comparisonPeriod?.supplement_triple_override
    ),
    supplementQuadOverride: normalizeSupplementOverride(
      comparisonPeriod?.supplementQuadOverride ?? comparisonPeriod?.supplement_quad_override
    ),
  }));
}

// Quote Response api to quote form data
/**
 * @param {*} supplierProductData
 * @param {*} quote
 */
function mapQuoteResponseToFormData(supplierProductData, quote) {
  // Initialize form data
  /** @type {Record<string, any>} */
  const formData = {
    id: null,
    environment: null,
    reference: null,
    client: {},
    periodName: null,
    cityStart: null,
    cityEnd: null,
    comparisonPeriods: [],
    version: null,
    period: {
      startYear: new Date().getFullYear().toString(),
      startMonth: null,
      startDay: null,
      endYear: null,
      endMonth: null,
      endDay: null,
    },
    nights: null,
    accommodations: [],
    title: null,
    folder: null,
    peopleRanges: [],
    program: {
      hideReference: false,
      pageImageBlocks: [],
      accommodationImageBlock: null,
      coverImageId: null,
      introduction: "",
      supernatureSuccesses: "",
      extraPages: [],
    },
    costs: [],
    logisticsCosts: [],
    margin: {
      status: "fixed",
      percentFixed: 0,
      percentDynamic: [],
      perPax: [],
      perGroup: [],
    },
    overviewOptions: {
      included: null,
      excluded: null,
      disclaimer: null,
    },
    notes: "",
    discountMargin: 30,
    supplementTripleOverride: null,
    supplementQuadOverride: null,
  };

  formData.id = quote.quote.id;
  formData.environment = quote.quote.environment || null;
  formData.reference = quote.quote.reference;
  formData.periodName = quote.quote.period_name ?? quote.quote.periodName ?? quote.quote.program?.period_name ?? null;
  formData.cityStart = quote.quote.city_start;
  formData.cityEnd = quote.quote.city_end;
  formData.version = quote.quote.version;
  formData.nights = quote.quote.number_of_nights;
  formData.title = quote.quote.title;
  formData.folder = quote.quote.folder;
  formData.margin.status = quote.quote.is_fixed_margin ? "fixed" : "dynamic";
  formData.margin.percentFixed = quote.quote.margin_percent;
  formData.client = quote.client;
  formData.overviewOptions.included = quote.quote.overview_included;
  formData.overviewOptions.excluded = quote.quote.overview_excluded;
  formData.overviewOptions.disclaimer = quote.quote.overview_disclaimer;
  formData.notes = quote.quote.notes;
  formData.discountMargin = quote.quote.discount_margin ?? 30;
  formData.supplementTripleOverride =
    quote.quote.supplement_triple_override != null ? Number(quote.quote.supplement_triple_override) : null;
  formData.supplementQuadOverride =
    quote.quote.supplement_quad_override != null ? Number(quote.quote.supplement_quad_override) : null;
  formData.updated_by = quote.account?.name;
  formData.updated_at = quote.quote.updated_at;
  formData.program.hideReference = quote.quote.program?.hide_reference ?? false;
  formData.program.coverImageId = quote.quote.program?.cover_image_id ?? null;
  formData.program.introduction = quote.quote.program?.introduction ?? "";
  formData.program.supernatureSuccesses = quote.quote.program?.supernature_successes ?? "";
  formData.program.extraPages = Array.isArray(quote.quote.program?.extra_pages)
    ? quote.quote.program.extra_pages.map(mapExtraPageToForm).filter(Boolean)
    : [];
  const savedAccommodationImageBlock = quote.quote.program?.accommodation_image_block;
  formData.program.accommodationImageBlock = savedAccommodationImageBlock
    ? mapApiBlockToForm(savedAccommodationImageBlock)
    : null;
  const savedPageImageBlocks = quote.quote.program?.page_image_blocks;
  if (Array.isArray(savedPageImageBlocks) && savedPageImageBlocks.length) {
    formData.program.pageImageBlocks = savedPageImageBlocks.map(mapApiBlockToForm);
  } else {
    const savedPageImages = quote.quote.program?.page_images;
    if (Array.isArray(savedPageImages) && savedPageImages.length) {
      formData.program.pageImageBlocks = migrateFlatPageImages(savedPageImages);
    } else {
      // Migrate legacy day-linked images into page image blocks.
      const programDaysForImages = quote.quote.program?.days ?? quote.quote.city_routes ?? [];
      const legacyDayImages = programDaysForImages
        .map((/** @type {*} */ day) => day?.image_id || null)
        .filter((/** @type {*} */ imageId) => imageId);
      formData.program.pageImageBlocks = legacyDayImages.length ? migrateFlatPageImages(legacyDayImages) : [];
    }
  }

  if (quote.quote.comparison_periods !== null && quote.quote.comparison_periods !== undefined) {
    try {
      const comparisonPeriods =
        typeof quote.quote.comparison_periods === "string"
          ? JSON.parse(quote.quote.comparison_periods)
          : quote.quote.comparison_periods;
      formData.comparisonPeriods = Array.isArray(comparisonPeriods)
        ? comparisonPeriods.slice(0, quoteUtils.MAX_COMPARISON_PERIODS).map(mapComparisonPeriodToFormData)
        : [];
    } catch (_) {
      formData.comparisonPeriods = [];
    }
  }

  // Start date
  if (quote.quote.start_date !== null) {
    const startDateObject = quote.quote.start_date;
    formData.period.startYear = startDateObject.year;
    formData.period.startMonth = startDateObject.month;
    formData.period.startDay = startDateObject.day;
  }

  // End date
  if (quote.quote.end_date !== null) {
    const endDateObject = quote.quote.end_date;
    formData.period.endYear = endDateObject.year;
    formData.period.endMonth = endDateObject.month;
    formData.period.endDay = endDateObject.day;
  }

  // Accommodation category
  if (quote.quote.accommodation_category !== null) {
    const accommodationCategoryObject = quote.quote.accommodation_category;
    const accommodationsToAdd = ["1", "1.5", "2", "2.5", "3", "3.5", "4", "4.5", "5"];
    accommodationsToAdd.forEach((accommodation) => {
      if (accommodationCategoryObject[accommodation]) {
        formData.accommodations.push(parseFloat(accommodation));
      }
    });
  }

  // People range
  if (quote.quote.people_ranges !== null) {
    const peopleRanges = JSON.parse(quote.quote.people_ranges);
    peopleRanges.forEach((/** @type {*} */ peopleRange) => {
      formData.peopleRanges.push(peopleRange);
    });
  }

  // Margin pax
  if (quote.quote.margin_pax !== null) {
    const marginPaxs = quote.quote.margin_pax;
    marginPaxs.forEach((/** @type {*} */ marginPax, /** @type {number} */ index) => {
      formData.margin.percentDynamic[index] = marginPax;
    });
  }

  const programDays = quote.quote.program?.days ?? quote.quote.city_routes ?? [];

  // Costs
  if (quote.costs !== null) {
    /** @type {Record<string, any[]>} */
    const days = {};
    for (let i = 0; i < quote.quote.number_of_nights + 1; i++) {
      days[i] = [];
    }
    quote.costs.forEach((/** @type {*} */ cost) => {
      days[cost.day].push(cost);
    });

    formData.costs = Object.keys(days).map((day) => ({
      cityStart: programDays[day]?.start,
      cityEnd: programDays[day]?.end,
      routeId: programDays[day]?.route_id,
      imageId: programDays[day]?.image_id,
      breakfast: programDays[day]?.breakfast ?? false,
      lunch: programDays[day]?.lunch ?? false,
      dinner: programDays[day]?.dinner ?? false,
      items: days[day].map((/** @type {*} */ item) => {
        const {
          uuid: itemUUID,
          supplier_id: itemSupplierId,
          supplier_name: itemSupplierName,
          supplier_city: itemSupplierCity,
          product_id: itemProductId,
          product_name: itemProductName,
          product_type: itemProductType,
          product_cost: itemProductCost,
          cost_type: itemCostType,
          show_in_program: itemIsShowInProgram,
          product_cost_is_editable: itemIsProductCostEditable,
          logistics_cost_uuids: itemLogisticsCostUUIDs,
        } = item;

        let supplier,
          supplierCity,
          product,
          productType,
          productCost,
          productProgramDescription,
          productProgramTitle,
          programSupplierName,
          costType,
          isShowInProgram,
          isProductEditableCost;

        isShowInProgram = itemIsShowInProgram;
        isProductEditableCost = itemIsProductCostEditable;

        if (itemSupplierId != null) {
          const { supplierInfo, productInfo } = lookupSupplierProduct(
            supplierProductData,
            itemSupplierId,
            itemProductId
          );

          if (supplierInfo) {
            supplier = {
              label: supplierInfo.name,
              value: itemSupplierId,
              foc: supplierInfo.meta?.foc,
              commission: supplierInfo.meta?.commission,
            };
            supplierCity = quoteUtils.isTBDSupplier(itemSupplierId) ? itemSupplierCity : supplierInfo.city;
            programSupplierName = quoteUtils.getProgramSupplierName(supplierInfo);
          } else {
            supplier = {
              label: itemSupplierName || String(itemSupplierId),
              value: itemSupplierId,
              foc: null,
              commission: null,
            };
            supplierCity = itemSupplierCity;
            programSupplierName = "";
          }

          const productTypeValue = productInfo?.product_type ?? itemProductType;
          product = { label: productInfo?.name ?? itemProductName, value: itemProductId };
          productType = productTypeValue;
          productCost = parseStoredProductCost(itemProductCost, productTypeValue);
          productProgramDescription = quoteUtils.getProductProgramDescription(productInfo, formData.period.startYear);
          productProgramTitle = quoteUtils.getProductProgramTitle(productInfo, formData.period.startYear);
        } else {
          supplier = { label: itemSupplierName, value: null, foc: null };
          supplierCity = itemSupplierCity;

          product = { label: itemProductName, value: null };
          productType = itemProductType;
          productProgramDescription = null;
          productProgramTitle = null;
          programSupplierName = "";
          productCost = parseStoredProductCost(itemProductCost, itemProductType);
        }

        costType = itemCostType;

        return quoteUtils.applyShowInProgramForProductType({
          uuid: itemUUID ?? quoteUtils.generateUUID(),
          supplier: supplier,
          supplierCity: supplierCity,
          product: product,
          productType: productType,
          productCost: productCost,
          costType: costType,
          productProgramDescription: productProgramDescription,
          productProgramTitle: productProgramTitle,
          programSupplierName: programSupplierName,
          isShowInProgram: isShowInProgram,
          isProductEditableCost: isProductEditableCost,
          logisticsCostUUIDs: itemLogisticsCostUUIDs ? JSON.parse(itemLogisticsCostUUIDs) : [],
        });
      }),
    }));
  }

  // Logistics costs
  if (quote.logistics_costs !== null) {
    formData.logisticsCosts = quote.logistics_costs.map((/** @type {*} */ item) => {
      const {
        uuid: itemUUID,
        supplier_id: itemSupplierId,
        supplier_name: itemSupplierName,
        supplier_city: itemSupplierCity,
        product_id: itemProductId,
        product_name: itemProductName,
        product_type: itemProductType,
        product_cost: itemProductCost,
        show_in_program: itemIsShowInProgram,
        product_cost_is_editable: itemIsProductCostEditable,
        days: itemDays,
      } = item;

      let supplier,
        supplierCity,
        product,
        productType,
        productCost,
        productProgramDescription,
        productProgramTitle,
        programSupplierName,
        isShowInProgram,
        isProductEditableCost;

      isShowInProgram = itemIsShowInProgram;
      isProductEditableCost = itemIsProductCostEditable;

      if (itemSupplierId != null) {
        const { supplierInfo, productInfo } = lookupSupplierProduct(supplierProductData, itemSupplierId, itemProductId);

        if (supplierInfo) {
          supplier = {
            label: supplierInfo.name,
            value: itemSupplierId,
            foc: supplierInfo.meta?.foc,
            commission: supplierInfo.meta?.commission,
          };
          supplierCity = quoteUtils.isTBDSupplier(itemSupplierId) ? itemSupplierCity : supplierInfo.city;
          programSupplierName = quoteUtils.getProgramSupplierName(supplierInfo);
        } else {
          supplier = {
            label: itemSupplierName || String(itemSupplierId),
            value: itemSupplierId,
            foc: null,
            commission: null,
          };
          supplierCity = itemSupplierCity;
          programSupplierName = "";
        }

        const productTypeValue = productInfo?.product_type ?? itemProductType;
        product = { label: productInfo?.name ?? itemProductName, value: itemProductId };
        productType = productTypeValue;
        productCost = parseStoredProductCost(itemProductCost, productTypeValue);
        productProgramDescription = quoteUtils.getProductProgramDescription(productInfo, formData.period.startYear);
        productProgramTitle = quoteUtils.getProductProgramTitle(productInfo, formData.period.startYear);
      } else {
        supplier = { label: itemSupplierName, value: null, foc: null };
        supplierCity = itemSupplierCity;

        product = { label: itemProductName, value: null };
        productType = itemProductType;
        productProgramDescription = null;
        productProgramTitle = null;
        programSupplierName = "";
        productCost = parseStoredProductCost(itemProductCost, itemProductType);
      }

      return quoteUtils.applyShowInProgramForProductType({
        uuid: itemUUID ?? quoteUtils.generateUUID(),
        supplier: supplier,
        supplierCity: supplierCity,
        product: product,
        productType: productType,
        productCost: productCost,
        productProgramDescription: productProgramDescription,
        productProgramTitle: productProgramTitle,
        programSupplierName: programSupplierName,
        isShowInProgram: isShowInProgram,
        isProductEditableCost: isProductEditableCost,
        days: itemDays != null ? JSON.parse(itemDays) : productType === "TRANS" || productType === "TOUR" ? [] : [0],
      });
    });
  }

  ensureComparisonPeriodsHaveMargins(formData);

  return formData;
}

/**
 * @param {*} formData
 * @param {*} workingEnv
 */
function mapFormDataToBody(formData, workingEnv) {
  const source = moneyService.getCurrencySource(workingEnv);
  const currencySymbol = source.currency_symbol;

  const body = {
    quote: {
      client_id: formData.client.id,
      reference: formData.reference,
      city_start: formData.cityStart,
      city_end: formData.cityEnd,
      version: formData.version,
      start_date: {
        day: formData.period.startDay,
        month: formData.period.startMonth,
        year: formData.period.startYear,
      },
      end_date: {
        day: formData.period.endDay,
        month: formData.period.endMonth,
        year: formData.period.endYear,
      },
      number_of_nights: formData.nights,
      comparison_periods: (formData.comparisonPeriods || [])
        .slice(0, quoteUtils.MAX_COMPARISON_PERIODS)
        .map(mapComparisonPeriodToBody),
      accommodation_category: {
        1: formData.accommodations.includes(1),
        1.5: formData.accommodations.includes(1.5),
        2: formData.accommodations.includes(2),
        2.5: formData.accommodations.includes(2.5),
        3: formData.accommodations.includes(3),
        3.5: formData.accommodations.includes(3.5),
        4: formData.accommodations.includes(4),
        4.5: formData.accommodations.includes(4.5),
        5: formData.accommodations.includes(5),
      },
      title: formData.title,
      folder: formData.folder,
      people_ranges: formData.peopleRanges,
      total_cost_pax: quoteUtils.getTotalCosts(formData.costs, formData.logisticsCosts, formData.peopleRanges),
      is_fixed_margin: formData.margin.status == "fixed",
      margin_percent: formData.margin.percentFixed,
      margin_pax: formData.margin.percentDynamic.map((/** @type {*} */ percentDynamic) => percentDynamic),
      overview_included: formData.overviewOptions.included,
      overview_excluded: formData.overviewOptions.excluded,
      overview_disclaimer: formData.overviewOptions.disclaimer,
      notes: formData.notes,
      discount_margin: formData.discountMargin,
      supplement_triple_override:
        formData.supplementTripleOverride != null && formData.supplementTripleOverride !== ""
          ? Number(formData.supplementTripleOverride)
          : null,
      supplement_quad_override:
        formData.supplementQuadOverride != null && formData.supplementQuadOverride !== ""
          ? Number(formData.supplementQuadOverride)
          : null,
      program: {
        hide_reference: formData.program?.hideReference ?? false,
        cover_image_id: formData.program?.coverImageId ?? null,
        period_name: formData.periodName,
        introduction: formData.program?.introduction ?? "",
        supernature_successes: formData.program?.supernatureSuccesses ?? "",
        page_image_blocks: (formData.program?.pageImageBlocks || []).map(mapFormBlockToApi),
        accommodation_image_block: formData.program?.accommodationImageBlock
          ? mapFormBlockToApi(formData.program.accommodationImageBlock)
          : mapFormBlockToApi({ imageCount: 3, imageIds: [] }),
        page_images: (() => {
          const blocks = formData.program?.pageImageBlocks;
          if (Array.isArray(blocks) && blocks.length) {
            return flattenPageImageBlocks(blocks);
          }
          return formData.costs
            .map((/** @type {*} */ cost) => cost.imageId)
            .filter((/** @type {*} */ imageId) => imageId);
        })(),
        extra_pages: (formData.program?.extraPages || []).map(mapExtraPageToApi).filter(Boolean),
        days: formData.costs.map((/** @type {*} */ cost) => ({
          start: cost.cityStart ?? "",
          end: cost.cityEnd ?? "",
          route_id: cost.routeId ?? null,
          image_id: cost.imageId ?? null,
          breakfast: !!cost.breakfast,
          lunch: !!cost.lunch,
          dinner: !!cost.dinner,
        })),
      },
    },

    costs: formData.costs.flatMap((/** @type {*} */ cost, /** @type {number} */ day) =>
      cost.items.map((/** @type {*} */ item, /** @type {number} */ order) => ({
        uuid: item.uuid,
        supplier_id: item.supplier.value ?? null,
        supplier_name: item.supplier.value ? null : item.supplier.label,
        supplier_city: item.supplier?.value && quoteUtils.isTBDSupplier(item.supplier.value) ? item.supplierCity : null,
        product_id: item.product.value ?? null,
        product_name: item.product.value ? null : item.product.label,
        product_type: item.productType,
        product_cost: {
          type: item.productCost.type,
          cur: item.productCost.currency,
          value: {
            default:
              item.productCost.value.default != null
                ? item.productType === "ROOM"
                  ? `${currencySymbol}${item.productCost.value.default * 2}`
                  : `${currencySymbol}${item.productCost.value.default}`
                : null,
            single:
              item.productCost.value.single != null
                ? item.productType === "ROOM"
                  ? `${currencySymbol}${item.productCost.value.single * 2}`
                  : `${currencySymbol}${item.productCost.value.single}`
                : null,
            twin:
              item.productCost.value.twin != null
                ? item.productType === "ROOM"
                  ? `${currencySymbol}${item.productCost.value.twin * 2}`
                  : `${currencySymbol}${item.productCost.value.twin}`
                : null,
            triple:
              item.productCost.value.triple != null
                ? item.productType === "ROOM"
                  ? `${currencySymbol}${item.productCost.value.triple * 2}`
                  : `${currencySymbol}${item.productCost.value.triple}`
                : null,
            quad:
              item.productCost.value.quad != null
                ? item.productType === "ROOM"
                  ? `${currencySymbol}${item.productCost.value.quad * 2}`
                  : `${currencySymbol}${item.productCost.value.quad}`
                : null,
          },
          porterage: item.productCost.porterage,
          valueIncludesPorterage: item.productCost.valueIncludesPorterage,
          confirm: item.productCost.confirm,
          rateKind: item.productCost.rateKind,
        },
        product_cost_is_editable: item.isProductEditableCost,
        show_in_program: item.isShowInProgram,
        day: day,
        order_in_day: order,
        logistics_cost_uuids: item.logisticsCostUUIDs,
        cost_type: item.costType,
      }))
    ),
    logistics_costs: formData.logisticsCosts.map((/** @type {*} */ item, /** @type {number} */ index) => ({
      uuid: item.uuid,
      supplier_id: item.supplier.value ?? null,
      supplier_name: item.supplier.value ? null : item.supplier.label,
      supplier_city: item.supplier?.value && quoteUtils.isTBDSupplier(item.supplier.value) ? item.supplierCity : null,
      product_id: item.product.value ?? null,
      product_name: item.product.value ? null : item.product.label,
      product_type: item.productType,
      product_cost: {
        type: item.productCost.type,
        cur: item.productCost.currency,
        value: {
          default:
            item.productCost.value.default != null
              ? item.productType === "ROOM"
                ? `${currencySymbol}${item.productCost.value.default * 2}`
                : `${currencySymbol}${item.productCost.value.default}`
              : null,
          single:
            item.productCost.value.single != null
              ? item.productType === "ROOM"
                ? `${currencySymbol}${item.productCost.value.single * 2}`
                : `${currencySymbol}${item.productCost.value.single}`
              : null,
          twin:
            item.productCost.value.twin != null
              ? item.productType === "ROOM"
                ? `${currencySymbol}${item.productCost.value.twin * 2}`
                : `${currencySymbol}${item.productCost.value.twin}`
              : null,
          triple:
            item.productCost.value.triple != null
              ? item.productType === "ROOM"
                ? `${currencySymbol}${item.productCost.value.triple * 2}`
                : `${currencySymbol}${item.productCost.value.triple}`
              : null,
          quad:
            item.productCost.value.quad != null
              ? item.productType === "ROOM"
                ? `${currencySymbol}${item.productCost.value.quad * 2}`
                : `${currencySymbol}${item.productCost.value.quad}`
              : null,
        },
        porterage: item.productCost.porterage,
        valueIncludesPorterage: item.productCost.valueIncludesPorterage,
        confirm: item.productCost.confirm,
        rateKind: item.productCost.rateKind,
      },
      product_cost_is_editable: item.isProductEditableCost,
      show_in_program: item.isShowInProgram,
      days: item.days,
      order: index,
    })),
  };

  return body;
}

export {
  mapQuoteResponseToFormData,
  mapFormDataToBody,
  cloneMargin,
  createEmptyComparisonPeriod,
  createEmptyMargin,
};
